import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectAllQuestionsForRound } from "features/question/questionSlice";
import { selectGameObject } from "features/gamestate/gameStateSlice";
import { GAMESTATE_ROUND_FINAL } from "features/gamestate/constants";
import {
  selectPanelSaveStatus,
  selectPanelLastSavedAt,
  selectPanelSaveErrorMsg,
} from "./editorSlice";
import { isClueWritten } from "./clueWritten";
import "./roundEditor.css";

const formatAge = (isoStr) => {
  if (!isoStr) return "";
  const diffMs = Date.now() - new Date(isoStr).getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 10) return "Saved just now";
  if (s < 60) return `Saved ${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `Saved ${m}m ago`;
  return `Saved ${Math.floor(m / 60)}h ago`;
};

const EditorToolbar = ({ round }) => {
  const gameObject = useSelector(selectGameObject);
  const questions = useSelector(selectAllQuestionsForRound(round));
  const saveStatus = useSelector(selectPanelSaveStatus);
  const lastSavedAt = useSelector(selectPanelLastSavedAt);
  const saveErrorMsg = useSelector(selectPanelSaveErrorMsg);
  const [ageLabel, setAgeLabel] = useState("");

  useEffect(() => {
    if (saveStatus !== "saved") return undefined;
    setAgeLabel(formatAge(lastSavedAt));
    const interval = setInterval(() => setAgeLabel(formatAge(lastSavedAt)), 30000);
    return () => clearInterval(interval);
  }, [saveStatus, lastSavedAt]);

  const isFinal = round === GAMESTATE_ROUND_FINAL;
  const title = isFinal
    ? "Editing Final Round"
    : `Editing Round ${round.replace("round", "")}`;

  let writtenCount = 0;
  let totalCount = 0;
  let ddCount = 0;
  let catCount = 0;

  if (isFinal) {
    totalCount = 1;
    if (isClueWritten(questions?.question)) writtenCount = 1;
    catCount = 1;
  } else {
    const cats = Array.isArray(gameObject?.categories?.[round])
      ? gameObject.categories[round]
      : [];
    catCount = cats.length;
    for (let row = 1; row <= 5; row += 1) {
      const rowQs = questions?.[row] || [];
      rowQs.forEach((q) => {
        totalCount += 1;
        if (isClueWritten(q)) writtenCount += 1;
        if (q?.dailyDouble) ddCount += 1;
      });
    }
  }

  const progressParts = [
    `${writtenCount} of ${totalCount} clues written`,
    ddCount > 0 && `${ddCount} Daily Double${ddCount !== 1 ? "s" : ""} placed`,
    `${catCount} categor${catCount !== 1 ? "ies" : "y"}`,
  ].filter(Boolean);

  const saveLabel =
    saveStatus === "saving"
      ? "Saving…"
      : saveStatus === "saved"
      ? ageLabel
      : saveStatus === "error"
      ? saveErrorMsg || "Save failed — try again"
      : null;

  return (
    <div className="re-toolbar">
      <span className="re-toolbar-title">{title}</span>
      <span className="re-toolbar-progress">{progressParts.join(" · ")}</span>
      <span className="re-toolbar-spacer" />
      {saveLabel && (
        <span
          className={`re-toolbar-save${saveStatus === "error" ? " is-error" : ""}`}
        >
          {saveLabel}
        </span>
      )}
    </div>
  );
};

export default EditorToolbar;
